import type {
  Assessment,
  CurrentUser,
  Person,
  PersonInvolvement,
} from "@thalamos/haven-common";
import {
  approverPartyForAssessor,
  guestAskOf,
  isAssessmentStatusUnderWay,
} from "@thalamos/haven-common";

import type { CurrentAssessmentRow } from "../../components/CurrentAssessments/CurrentAssessments.js";
import { latestSubmission } from "../Assessment/latestSubmission.js";
import {
  assessorNameOf,
  currentAssessmentActions,
} from "./involvementManagement.js";

/**
 * When an assessment was last acted on in a way anybody reading it can see:
 * started, submitted or decided. Saving a draft does not count, because a
 * draft is its owner's alone.
 */
export const lastActivityOf = (assessment: Assessment): Date => {
  const latest = latestSubmission(assessment);

  return [latest?.submittedAt, latest?.decision?.decidedAt].reduce<Date>(
    (last, at) => (at !== undefined && at > last ? at : last),
    assessment.startedAt,
  );
};

/**
 * The young person's assessments still in the workflow: the definitive one
 * first, as the one that will stand, then the rest by most recent activity.
 */
export const currentAssessments = (
  assessments: readonly Assessment[],
): Assessment[] =>
  assessments
    .filter(
      (assessment) =>
        assessment.deletion === null &&
        isAssessmentStatusUnderWay(assessment.status),
    )
    .toSorted(
      (a, b) =>
        Number(b.definitive) - Number(a.definitive) ||
        lastActivityOf(b).getTime() - lastActivityOf(a).getTime() ||
        a.id.localeCompare(b.id),
    );

/**
 * The rows of the current assessments section, in order, each with who it
 * names and what the session is offered on it.
 */
export const currentAssessmentRows = (
  assessments: readonly Assessment[],
  user: CurrentUser | undefined,
  involvements: PersonInvolvement[] | undefined,
  person?: Person,
): CurrentAssessmentRow[] => {
  const actionsOn = currentAssessmentActions(user, involvements, person);

  return currentAssessments(assessments).map((assessment) => ({
    assessment,
    assessorName: assessorNameOf(assessment, user, involvements),
    approverName: approverPartyForAssessor(
      involvements ?? [],
      assessment.ownerUserId,
    )?.name,
    guestAsk: guestAskOf(assessment, involvements),
    actions: actionsOn(assessment),
  }));
};
